// refusal — how the browser READS a refused answer (the error envelope,
// ADR-018) by its SHAPE alone: the status, the body's code, the retry hint.
// One home, so apiClient, the live client, the retry policy and the bench's
// simulated people read the same thing — a refusal is
// `{ response: { status, data, headers } }` whether axios or the bench's own
// fetch raised it. No axios here: the live client stays free of the app's
// singletons, and the bench runs this file in Node.
//
// The busy answer (the fuse, ADR-079 §5) is told by the body's CODE, never by
// the status: a proxy's own 503 page carries no code and is not one.

interface RefusedResponse {
  status: number | null;
  data: unknown;
  headers: Record<string, unknown>;
}

/** The answer a refusal carries, or null for a failure with no answer (the
 *  transport, a fault of our own code). */
export function refusedResponseOf(error: unknown): RefusedResponse | null {
  const response = (error as { response?: unknown } | null | undefined)?.response;
  if (typeof response !== 'object' || response === null) return null;
  const { status, data, headers } = response as { status?: unknown; data?: unknown; headers?: unknown };
  return {
    status: typeof status === 'number' ? status : null,
    data,
    headers: typeof headers === 'object' && headers !== null ? (headers as Record<string, unknown>) : {},
  };
}

/** The HTTP status of a refused answer; null for a failure with no answer. */
export function statusOf(error: unknown): number | null {
  return refusedResponseOf(error)?.status ?? null;
}

/** The body's typed code (errors.json keys ARE these codes); null when the
 *  body is not an envelope — a proxy's HTML page, an empty body. */
export function codeOf(error: unknown): string | null {
  const code = (refusedResponseOf(error)?.data as { code?: unknown } | null | undefined)?.code;
  return typeof code === 'string' ? code : null;
}

/** The retry hint a refusal carries, in seconds — the body's `retry_after_s`
 *  first, then the `Retry-After` header; null when it carries none. */
export function retryAfterOf(error: unknown): number | null {
  const response = refusedResponseOf(error);
  if (!response) return null;
  const details = (response.data as { details?: { retry_after_s?: unknown } } | null | undefined)?.details;
  const fromBody = details?.retry_after_s;
  if (typeof fromBody === 'number' && Number.isFinite(fromBody) && fromBody > 0) return fromBody;
  const fromHeader = Number(response.headers['retry-after']);
  return Number.isFinite(fromHeader) && fromHeader > 0 ? fromHeader : null;
}

/** A failure with NO answer — the transport (the network down, the timeout, an aborted request): the client's own
 *  error with no response. Worded by the shared mapping (its generic line), never by the client's English. */
export function isTransportFailure(error: unknown): boolean {
  return (error as { isAxiosError?: unknown } | null | undefined)?.isAxiosError === true && refusedResponseOf(error) === null;
}

/** Is this failure the server's BUSY answer (ADR-079)? */
export function isBusy(error: unknown): boolean {
  return statusOf(error) === 503 && codeOf(error) === 'BUSY';
}

/** Is this the PROXY answering for an absent server — a 502 · 503 · 504 that
 *  carries no envelope (Traefik's own `503 text/plain` while the backend is
 *  down)? Not busy (no code), not a fault of ours: "unavailable", calmly. */
export function isServerUnavailable(error: unknown): boolean {
  const status = statusOf(error);
  return (status === 502 || status === 503 || status === 504) && codeOf(error) === null;
}

/** A BUSY answer always carries its hint; this is the net under a missing one (seconds). */
export const BUSY_FALLBACK_S = 5;
/** The jitter around the hint: ± 20 %, so the tabs that met the same busy moment do not return together. */
export const BUSY_JITTER = 0.2;

/** The delay before TanStack's ONE retry (lib/queryClient.ts — the bench's
 *  people use the same): after a BUSY answer, the server's hint ± 20 %;
 *  anything else keeps TanStack's own backoff. */
export function retryDelayFor(attempt: number, error: unknown, random: () => number = Math.random): number {
  if (isBusy(error)) {
    const hintS = retryAfterOf(error) ?? BUSY_FALLBACK_S;
    return Math.round(hintS * 1000 * (1 - BUSY_JITTER + 2 * BUSY_JITTER * random()));
  }
  // TanStack's own default backoff, kept as it is.
  return Math.min(1000 * 2 ** attempt, 30_000);
}
